import React, { Children, cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';
import './BlurReveal.css';

/**
 * BlurReveal — text that materialises glyph by glyph: each character rises
 * out of a blur with a small lift, staggered left to right.
 *
 * Two variants, both from 21st.dev "blur-reveal" components (Motion):
 * - `glyphs` (default, @yoavzaza-style) — the text is split and every
 *   character animates on its own stagger.
 * - `block` (@badtzx0-style) — the children stay as they are and the whole
 *   element rises out of the blur after `delay`; use it to sequence a
 *   section (eyebrow → copy → buttons) around a glyph headline.
 *
 * CSS-only adaptation for the DS: timings come from the transition tokens, the blur from the effect
 * styles, the lift from the spacing scale, and `prefers-reduced-motion`
 * shows the text at once. Only the enter animation is kept — hero headlines
 * never leave the page.
 *
 * - `children` may be a string or elements wrapping strings (an accent
 *   `<span>`): wrappers are kept, so their colour / class still applies,
 *   and the glyph index keeps counting across them.
 * - Words are `inline-block; white-space: nowrap` groups (a word's trailing
 *   space rides with it), so the line breaks exactly where plain text would.
 * - The full text is kept for assistive tech in a visually hidden span; the
 *   animated glyphs are `aria-hidden`.
 */
const BlurReveal = ({
  children,
  as: Tag = 'p',
  className = '',
  delay = 0,
  stagger,
  duration,
  variant = 'glyphs',
  unit = 'glyph',
  easing = 'ui',
  distance,
  blurRadius,
  lift = true,
  blur = true,
  style,
  ...rest
}) => {
  const counter = { i: 0 };

  const glyph = (char, key) => (
    <span
      key={key}
      className="blur-reveal__glyph"
      // eslint-disable-next-line no-plusplus
      style={{ '--blur-reveal-i': counter.i++ }}
    >
      {char}
    </span>
  );

  // "ab cd" → [ab␣][cd]: a word group per run of non-space characters, the
  // whitespace that follows a word is appended to the same group. With
  // unit="word" the whole word (+ its space) is ONE animated span.
  const splitText = (text, keyPrefix) => {
    const groups = [];
    let current = null;
    text.split(/(\s+)/).forEach((token, t) => {
      if (!token) return;
      if (/^\s+$/.test(token)) {
        if (unit === 'word') {
          if (current) current.push(' ');
          else groups.push([' ']);
          return;
        }
        const space = glyph(' ', `${keyPrefix}-s${t}`);
        if (current) current.push(space);
        else groups.push([space]);
        return;
      }
      current = unit === 'word'
        ? [token]
        : Array.from(token, (char, c) => glyph(char, `${keyPrefix}-${t}-${c}`));
      groups.push(current);
    });
    return groups.map((nodes, g) => (
      <span key={`${keyPrefix}-w${g}`} className="blur-reveal__word">
        {unit === 'word' ? glyph(nodes.join(''), `${keyPrefix}-${g}`) : nodes}
      </span>
    ));
  };

  const walk = (nodes, keyPrefix) =>
    Children.toArray(nodes).map((node, n) => {
      const key = `${keyPrefix}-${n}`;
      if (typeof node === 'string' || typeof node === 'number') return splitText(String(node), key);
      if (isValidElement(node)) {
        return cloneElement(node, { key }, walk(node.props.children, key));
      }
      return null;
    });

  const plainText = (nodes) =>
    Children.toArray(nodes)
      .map((node) => {
        if (typeof node === 'string' || typeof node === 'number') return String(node);
        if (isValidElement(node)) return plainText(node.props.children);
        return '';
      })
      .join('');

  // iOS Safari can keep a stale blurred layer on a unit after its filter
  // animation has ended (one word of the OVER/LL hero stayed blurry). Once a
  // unit has landed, drop its animation + filter altogether so WebKit
  // re-rasterises the text sharp. DOM class, not state: React never rewrites
  // a className it did not change, and a re-render must not restart anything.
  const settle = (e) => {
    if (e.animationName === 'blur-reveal-lift' || e.animationName === 'blur-reveal-lines') {
      e.target.classList.add('blur-reveal--settled');
    }
  };

  const mods = `${lift ? '' : ' blur-reveal--still'}${blur ? '' : ' blur-reveal--clear'}${
    easing === 'spring' ? ' blur-reveal--spring' : ' blur-reveal--ui'
  }`;
  const vars = { ...style };
  if (distance != null) vars['--blur-reveal-lift'] = distance;
  if (blurRadius != null) vars['--blur-reveal-blur'] = blurRadius;
  if (delay) vars['--blur-reveal-delay'] = `${delay}ms`;
  if (stagger != null) vars['--blur-reveal-stagger'] = `${stagger}ms`;
  if (duration != null) vars['--blur-reveal-duration'] = `${duration}ms`;

  if (variant === 'block') {
    return (
      <Tag
        className={`blur-reveal blur-reveal--block${mods}${className ? ` ${className}` : ''}`}
        style={vars}
        onAnimationEnd={settle}
        {...rest}
      >
        {children}
      </Tag>
    );
  }

  if (variant === 'lines') {
    // Each top-level chunk (a string, or an element such as the accent span)
    // rises out of its own clipping box — a baseline mask, no fade, no blur.
    const chunks = Children.toArray(children).map((node, i) => (
      <span key={`l${i}`} className="blur-reveal__line">
        <span className="blur-reveal__line-inner" style={{ '--blur-reveal-i': i }}>
          {node}
        </span>
      </span>
    ));
    return (
      <Tag
        className={`blur-reveal blur-reveal--lines${mods}${className ? ` ${className}` : ''}`}
        style={vars}
        onAnimationEnd={settle}
        {...rest}
      >
        {chunks}
      </Tag>
    );
  }

  return (
    <Tag className={`blur-reveal${mods}${className ? ` ${className}` : ''}`} style={vars} onAnimationEnd={settle} {...rest}>
      <span className="blur-reveal__sr">{plainText(children)}</span>
      <span aria-hidden="true">{walk(children, 'g')}</span>
    </Tag>
  );
};

BlurReveal.propTypes = {
  /** glyphs: a string, or elements (e.g. an accent span) wrapping strings; block: anything */
  children: PropTypes.node.isRequired,
  /** Rendered tag (h1, h2, p, …) */
  as: PropTypes.elementType,
  /** Additional CSS class — put the typo-* class here */
  className: PropTypes.string,
  /** Wait before the first glyph, ms */
  delay: PropTypes.number,
  /** Gap between consecutive glyphs, ms (default 16) */
  stagger: PropTypes.number,
  /** Per-unit (or block) duration, ms (default 1.8 × --transition-time-button = 720) */
  duration: PropTypes.number,
  /** glyphs — split text, staggered units; block — children untouched, one rise after `delay`; lines — each top-level chunk rises out of a baseline mask (no fade/blur, text stays in the DOM) */
  variant: PropTypes.oneOf(['glyphs', 'block', 'lines']),
  /** glyphs variant: what gets its own stagger step — a character, or a whole word (+ its space) */
  unit: PropTypes.oneOf(['glyph', 'word']),
  /** ui (default) — the UI ease-out (--transition-function-ui); spring — the DS Gentle spring (--transition-function-drawer) */
  easing: PropTypes.oneOf(['spring', 'ui']),
  /** Movement distance override (CSS length, e.g. 'var(--space-ui-small-m)'); default small-s */
  distance: PropTypes.string,
  /** Enter blur override (CSS length, e.g. 'calc(var(--blur-sticky-footer-m2) / 6)'); default ⅙ of the M2 blur (2px) */
  blurRadius: PropTypes.string,
  /** false — fade out of the blur in place, no movement (e.g. a hero image) */
  lift: PropTypes.bool,
  /** false — no blur filter, fade (+ lift) only; use for video, where a filter over playing frames is costly and looks off */
  blur: PropTypes.bool,
  /** Inline styles merged with the timing variables */
  style: PropTypes.object,
};

export default BlurReveal;
